import {
  Box,
  Button,
  Divider,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useContext, useState, useEffect, useRef } from 'react';
import { ActivityContext } from '@/contexts/ActivityContext';
import { ActivityFormContext, ActivityForms } from '@/contexts/ActivityFormContext';
import AddIcon from '@mui/icons-material/Add';
import MoreOptionsHeader from './MoreOptionsHeader';
import ImageInput from './imageInput/ImageInput';
import SubtaskText from './SubtaskText';
import EditIcon from '@mui/icons-material/Edit';
import { validateDescription } from '@/components/utils/Validate';
import { Cancel, EditLocation } from '@mui/icons-material';
import LocationsDialog from './location/LocationsDialog';
import LocationCard from './location/LocationCard';
import { AiApi, languageKey, SubtaskItem } from '@api';
import AssistantSuggestionPopper from '@/components/assistant/AssistantSuggestionPopper';
import AssistantSuggestion from '@/components/assistant/AssistantSuggestion';
import { useAiUsable } from '@/utils/ai';

type MoreOptionsFormProps = {
  noHeader?: boolean;
  validateForm?: boolean;
};

const MoreOptionsForm = ({ noHeader = false, validateForm = true }: MoreOptionsFormProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const isLanguageKey = (lang: any): lang is languageKey => {
    return ['sv', 'en', 'de', 'fr', 'es', 'it', 'pl', 'nb', 'da', 'fi'].includes(lang);
  };
  const { palette } = useTheme();
  const { activity, setActivity } = useContext(ActivityContext);
  const formHandler = useContext(ActivityFormContext);
  const [descriptionError, setDescriptionError] = useState<boolean | string>(false);
  const [description, setDescription] = useState<string>(activity.description);
  const [imageError, setImageError] = useState<boolean>(false);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [addressError, setAddressError] = useState<boolean | string>(false);

  const [locationsDialog, setLocationsDialogOpen] = useState(false);

  // Se utils/ai.ts: nyckel i miljön OCH samtycke, annars göms förslaget.
  const aiEnabled = useAiUsable();
  const [detailsSuggestion, setDetailsSuggestion] = useState<{
    description: string;
    subtasks: SubtaskItem[];
  } | null>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(true);
  const [detailsError, setDetailsError] = useState<boolean>(false);
  const [showDescSuggestion, setShowDescSuggestion] = useState<boolean>(true);
  const [showSubtaskSuggestion, setShowSubtaskSuggestion] = useState<boolean>(true);

  const addSubtasks = () => {
    formHandler.goToForm(ActivityForms.AddSubtasks);
  };

  const setImage = (file: File) => {
    setImageError(false);
    if (file) {
      // The user uploaded an image
      if (file.type && file.type.startsWith('image/')) {
        setActivity({
          ...activity,
          picture: URL.createObjectURL(file),
          pictureFile: file,
          pictureEdited: true,
        });
      } else {
        setImageError(true);
        //Clear image from activity
        setActivity({ ...activity, picture: '', pictureFile: undefined, pictureEdited: true });
      }
    }
  };

  const fetchDetailsSuggestions = async () => {
    setDetailsLoading(true);
    setShowDescSuggestion(true);
    setShowSubtaskSuggestion(true);
    try {
      const data = await new AiApi().activityDetailsSuggestion(
        isLanguageKey(lang) ? lang : 'sv',
        activity,
      );
      if (data) {
        setDetailsSuggestion({
          description: data.description,
          subtasks: data.subtasks,
        });
      } else {
        setDescriptionError(true);
      }
    } catch (error) {
      console.error('Error fetching suggestions', error);
      setDescriptionError(true);
    } finally {
      setDetailsLoading(false);
    }
  };

  //Validate form
  useEffect(() => {
    const descriptionValid = validateDescription(description, t); // Null = valid
    if (descriptionValid === null) {
      setActivity({ ...activity, description: description });
      setDescriptionError(false);
    } else {
      setDescriptionError(descriptionValid);
      if (!validateForm) {
        setActivity({ ...activity, description: description });
      }
    }

    //We dont need to validate the form
    if (!validateForm) {
      return;
    }
    if (
      activity.activityName.trim().length &&
      descriptionValid === null &&
      imageLoading === false &&
      !addressError
    ) {
      formHandler.setValid(true);
    } else {
      formHandler.setValid(false);
    }
  }, [description, imageLoading, addressError]);

  useEffect(() => {
    fetchDetailsSuggestions();
  }, []);

  useEffect(() => {
    if (!showDescSuggestion && !showSubtaskSuggestion) {
      formHandler.setAssistantAnchorEl?.(null);

      fetchDetailsSuggestions();
    }
  }, [showDescSuggestion, showSubtaskSuggestion]);

  return (
    <>
      {noHeader ? null : <MoreOptionsHeader />}
      <ImageInput
        id="addImage"
        value={activity.picture}
        onChange={setImage}
        sx={{ mx: 'auto', width: '75%', maxWidth: '14em', height: null, aspectRatio: '1/1' }}
        onRemove={() => setActivity({ ...activity, picture: '', pictureFile: undefined })}
        imageLoading={setImageLoading}
        aiDesc={`Activity name: ${activity.activityName} Description: ${activity.description}`}
      />
      {imageError && (
        <Typography color="error" variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
          {t('Only images are allowed')}
        </Typography>
      )}
      <InputLabel htmlFor="addTime" sx={{ mt: 4 }}>
        {t('Activity description')}
      </InputLabel>
      <TextField
        id="activityDescription"
        multiline
        maxRows={5}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={`${t('Add a description')}..`}
        inputProps={{ maxLength: 200 }}
        error={!!descriptionError}
        helperText={descriptionError}
        onKeyDown={(event) => {
          if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
        }}
      />
      <Typography
        variant="caption"
        display="block"
        gutterBottom
        sx={{ display: 'flex', justifyContent: 'end', marginTop: '4px' }}
      >
        {description.length}/200
      </Typography>

      <InputLabel sx={{ mt: 2 }}>{t('Add subtasks')}</InputLabel>
      <Button
        variant="outlined"
        id="addSubtasks"
        onClick={addSubtasks}
        startIcon={activity.subtasks?.length ? <EditIcon /> : <AddIcon />}
        size="large"
      >
        {activity.subtasks?.length ? t('Edit subtasks') : t('Add subtasks')}
      </Button>
      {activity.subtasks?.length ? <SubtaskText /> : null}
      <InputLabel sx={{ mt: 3 }}>{t('Activity location')}</InputLabel>
      {activity.location ? (
        <LocationCard
          key={activity.location.locationId}
          location={activity.location}
          onIconClick={() => {
            setActivity({ ...activity, location: null });
          }}
          icon={<Cancel />}
          isClickable={false}
          ariaLabel={t('Cancel') as string}
        />
      ) : (
        <Button
          variant="outlined"
          id="locations"
          onClick={() => setLocationsDialogOpen(true)}
          startIcon={<EditLocation />}
          size="large"
          sx={{ width: '100%' }}
        >
          {t('My locations')}
        </Button>
      )}
      <LocationsDialog open={locationsDialog} onClose={() => setLocationsDialogOpen(false)} />
      {aiEnabled && (
        <AssistantSuggestionPopper
          anchorEl={formHandler.assistantAnchorEl ?? null}
          loading={detailsLoading}
          open={!!detailsSuggestion && !!formHandler.assistantAnchorEl}
        >
          {detailsError ? (
            <Typography variant="body2">{t('No suggestions available')}</Typography>
          ) : (
            <Stack spacing={2}>
              {showDescSuggestion && (
                <AssistantSuggestion
                  title={t('Suggested description:')}
                  onAccept={() => {
                    if (detailsSuggestion?.description) {
                      setDescription(detailsSuggestion.description);
                    }
                    setShowDescSuggestion(false);
                  }}
                  onDecline={() => {
                    setShowDescSuggestion(false);
                  }}
                  autoClose={false}
                  loading={detailsLoading}
                >
                  <Typography variant="body2" color={palette.grey[50]}>
                    {detailsSuggestion?.description}
                  </Typography>
                </AssistantSuggestion>
              )}

              {showDescSuggestion && showSubtaskSuggestion && <Divider />}

              {showSubtaskSuggestion && (
                <AssistantSuggestion
                  title={t('Suggested subtasks:') || 'Suggested subtasks:'}
                  onAccept={() => {
                    if (detailsSuggestion?.subtasks) {
                      setActivity({ ...activity, subtasks: detailsSuggestion.subtasks });
                    }
                    setShowSubtaskSuggestion(false);
                  }}
                  onDecline={() => {
                    setShowSubtaskSuggestion(false);
                  }}
                  autoClose={false}
                  loading={detailsLoading}
                >
                  <Box>
                    {detailsSuggestion?.subtasks.map((subtask, index) => (
                      <Typography key={index} variant="body2" color={palette.grey[50]}>
                        • {subtask.subtaskText}
                      </Typography>
                    ))}
                  </Box>
                </AssistantSuggestion>
              )}
            </Stack>
          )}
        </AssistantSuggestionPopper>
      )}
    </>
  );
};

export default MoreOptionsForm;
